import React, { FC, useState } from 'react';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { Box } from '@mui/material';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import { DataGrid, GridToolbar, GridColDef, GridActionsCellItem } from '@mui/x-data-grid';
import { esES } from '@mui/x-data-grid/locales';

import { hasPermission } from '@relif/zeller-roles';
import { PrimaryBtn } from 'components/UI/Buttons';
import useCurrentUser from 'hooks/useCurrentUser';
import { useGetUserBusinessUnitsQuery } from 'services/api/businessUnits.api';
import { useGetUsersQuery, useDeleteUserMutation } from 'services/api/user.api';
import { User } from 'services/types/user';
import removeCSVInjection from 'utils/removeCSVInjection';

import UserFormModal from './UserFormModal';

const SELLER_MANAGEMENT_ATTRIBUTES: GridColDef<User>[] = [
  {
    field: 'brandsToSell',
    headerName: 'Marcas que vende',
    width: 200,
    valueGetter: (brandsToSell?: string[] | null) => {
      return brandsToSell?.join(', ');
    },
  },
  {
    field: 'topicsOfInterest',
    headerName: 'Temas de interés',
    width: 200,
    valueGetter: (topicsOfInterest?: string[] | null) => {
      return topicsOfInterest?.join(', ');
    },
  },
  {
    field: 'branchOffice',
    headerName: 'Sucursal',
    width: 100,
  },
];

const USER_ATTRIBUTES: GridColDef<User>[] = [
  {
    field: 'name',
    headerName: 'Nombre',
    width: 150,
    valueGetter: (name?: string | null, row?: User) => {
      return `${row?.isAdmin ? '[ADMIN] ' : ''}${name || '-'}`;
    },
  },
  {
    field: 'email',
    headerName: 'Email',
    width: 300,
  },
  {
    field: 'businessUnitNames',
    headerName: 'Unidades de negocio',
    width: 250,
    valueGetter: (businessUnitNames?: string[] | null) => {
      return businessUnitNames?.join(', ');
    },
  },
  {
    field: 'role',
    headerName: 'Rol',
    width: 200,
    valueGetter: (role?: string | null) => {
      if (role === 'seller') return 'Vendedor';
      if (role === 'sellerWithAssignment') return 'Vendedor con Asignación';
      if (role === 'branchManager') return 'Gerente de Sucursal';
      if (role === 'businessUnitAdmin') return 'Administrador de Negocio';
      return role?.toUpperCase();
    },
  },
  {
    field: 'isAssignable',
    headerName: 'Asignable',
    width: 120,
    valueFormatter: (value) => (value ? 'Si' : 'No'),
  },
];

const USER_ATTRIBUTES_ADMIN: GridColDef<User>[] = [
  {
    field: 'id',
    headerName: 'ID',
    width: 60,
  },
  {
    field: 'company',
    headerName: 'Empresa',
    width: 200,
    valueGetter: (company?: { id: number; name: string } | null) => {
      return company?.name;
    },
  },
  {
    field: 'companyId',
    headerName: 'Empresa',
    width: 100,
  },
  {
    field: 'filterConversationsByDefault',
    headerName: 'Filtrar conversaciones activado?',
    width: 300,
    valueFormatter: (value) => (value ? 'Si' : 'No'),
  },
];

const UsersTable: FC = () => {
  const { data: users, refetch } = useGetUsersQuery(null);
  const { data: businessUnits } = useGetUserBusinessUnitsQuery(null);
  const [deleteUser] = useDeleteUserMutation();
  const currentUser = useCurrentUser();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  const usersRows = (users || []).map((user) => ({
    ...user,
    email: removeCSVInjection(user.email || ''),
  }));

  const handleCreateUser = () => {
    setSelectedUser(null);
    setModalOpen(true);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setModalOpen(true);
  };

  const handleDeleteUser = (user: User) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!userToDelete) return;

    try {
      await deleteUser(userToDelete.id).unwrap();
      await refetch();
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[UsersTable] Error deleting user:', error);
    }
  };

  const actionsColumn: GridColDef<User> = {
    field: 'actions',
    type: 'actions',
    headerName: 'Acciones',
    width: 120,
    getActions: (params) => {
      const user = params.row;
      const actions = [];

      if (currentUser && hasPermission(currentUser, 'users', 'update', user)) {
        actions.push(
          <GridActionsCellItem key="edit" icon={<EditIcon />} label="Editar" onClick={() => handleEditUser(user)} />
        );
      }

      if (currentUser && hasPermission(currentUser, 'users', 'delete', user)) {
        actions.push(
          <GridActionsCellItem
            key="delete"
            icon={<DeleteIcon />}
            label="Eliminar"
            onClick={() => handleDeleteUser(user)}
          />
        );
      }

      return actions;
    },
  };

  const hasSellerManagement = businessUnits?.some((businessUnit) => businessUnit.hasSellerManagement);

  const userAttributes = currentUser?.isAdmin
    ? [...USER_ATTRIBUTES, ...USER_ATTRIBUTES_ADMIN, ...SELLER_MANAGEMENT_ATTRIBUTES, actionsColumn]
    : [...USER_ATTRIBUTES, ...(hasSellerManagement ? SELLER_MANAGEMENT_ATTRIBUTES : []), actionsColumn];

  return (
    <Box style={{ width: '100%' }} my={2}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mt={2}
        sx={{ ml: { xs: 6, sm: 10, md: 2 }, mr: { xs: 6, sm: 10, md: 2 } }}
      >
        <Typography variant="h4">Lista de Usuarios</Typography>
        {currentUser && hasPermission(currentUser, 'users', 'create', null) && (
          <PrimaryBtn onClick={handleCreateUser}>Crear Usuario</PrimaryBtn>
        )}
      </Box>

      <Box
        sx={{
          backgroundColor: 'White',
          borderRadius: 4,
          p: 2,
        }}
      >
        <DataGrid
          rows={usersRows}
          columns={userAttributes}
          slots={{
            toolbar: GridToolbar,
          }}
          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            color: 'Black',
            height: 'calc(100vh - 150px)',
            '& .MuiDataGrid-row': {
              cursor: 'pointer',
            },
            '& .MuiDataGrid-row.Mui-selected': {
              backgroundColor: '#e8f0fe',
            },
            border: 'none',
          }}
        />
      </Box>

      <UserFormModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        user={selectedUser}
        onSuccess={() => {
          // eslint-disable-next-line no-console
          refetch().catch((error) => console.error('[UsersTable] Error refetching users:', error));
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar al usuario &quot;{userToDelete?.name || userToDelete?.email}&quot;?
            Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={() => {
              // eslint-disable-next-line no-console
              confirmDelete().catch(console.error);
            }}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UsersTable;
